// Copyright 2025, University of Maryland, All Rights Reserved
import { Upload } from 'lucide-react';
import { useMutation } from '@tanstack/react-query';
import { configApi } from '../api/config';

interface MigrationModalProps {
  show: boolean;
  onClose: () => void;
}

export function MigrationModal({ show, onClose }: MigrationModalProps) {
  const migrateConfigs = useMutation({
    mutationFn: () => configApi.migrateConfigs(),
  });

  const { isPending, data } = migrateConfigs;

  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl">
        <h3 className="text-lg font-semibold mb-4 flex items-center space-x-2">
          <Upload className="w-5 h-5" />
          <span>Migrate Local Configurations to S3</span>
        </h3>

        {isPending ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p>Migrating configurations...</p>
          </div>
        ) : data ? (
          <div className="space-y-4">
            <div className={`p-4 rounded-lg ${data.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
              <p className={`font-medium ${data.success ? 'text-green-800' : 'text-red-800'}`}>
                {data.message}
              </p>
            </div>

            <div className="grid grid-cols-3 gap-4 text-sm">
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">{data.total_migrated}</div>
                <div className="text-gray-600">Migrated</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-yellow-600">{data.total_skipped}</div>
                <div className="text-gray-600">Skipped</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-red-600">{data.total_errors}</div>
                <div className="text-gray-600">Errors</div>
              </div>
            </div>

            {data.migrated_templates?.length > 0 && (
              <div>
                <h4 className="font-medium text-green-800 mb-2">✅ Migrated Templates:</h4>
                <ul className="text-sm text-green-700 space-y-1">
                  {data.migrated_templates.map((template: string, idx: number) => (
                    <li key={idx}>• {template}</li>
                  ))}
                </ul>
              </div>
            )}

            {data.migrated_prompts?.length > 0 && (
              <div>
                <h4 className="font-medium text-green-800 mb-2">✅ Migrated Prompts:</h4>
                <ul className="text-sm text-green-700 space-y-1">
                  {data.migrated_prompts.map((prompt: string, idx: number) => (
                    <li key={idx}>• {prompt}</li>
                  ))}
                </ul>
              </div>
            )}

            {data.errors?.length > 0 && (
              <div>
                <h4 className="font-medium text-red-800 mb-2">❌ Errors:</h4>
                <ul className="text-sm text-red-700 space-y-1">
                  {data.errors.map((error: string, idx: number) => (
                    <li key={idx}>• {error}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        ) : (
          <div>
            <p className="text-gray-600 mb-4">
              This will migrate existing local templates and prompts to S3 default storage.
              Existing S3 configurations will be skipped.
            </p>
            <div className="bg-amber-50 border border-amber-200 rounded-md p-3 mb-4">
              <p className="text-amber-800 text-sm">
                <strong>Note:</strong> This operation requires admin privileges and will only migrate
                items that don't already exist in S3.
              </p>
            </div>
          </div>
        )}

        <div className="flex justify-end space-x-3 mt-6">
          <button
            onClick={() => {
              onClose();
              if (data) migrateConfigs.reset();
            }}
            className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
          >
            Close
          </button>
          {!data && !isPending && (
            <button
              onClick={() => migrateConfigs.mutate()}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Start Migration
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
